import { entityService } from '@/services/EntityService';
import { iemService } from '@/services/iem';
import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import {
  buildTotalClothingSizeByWorkforceCategoryHtml,
  type ParentNsnSizeGroup,
} from '@/reports/templates/totalClothingSizeByWorkforceCategoryTemplate';
import type { Entity } from '@/types/Entity';

interface PersonSizingEntityData {
  person_id?: string;
  catalog_id?: string;
  parent_id?: string;
}

const normalizeStringParam = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeStringArrayParam = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
};

const chunk = <T>(values: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const buildIdFilter = (ids: string[]): string =>
  ids.length === 1 ? `eq.${ids[0]}` : `in.(${ids.join(',')})`;

/** Person ids whose Workforce Category (and optional Section) match the given filters. */
const findApplicablePersonIds = async (
  workforceCategory: string,
  section: string | null,
): Promise<string[]> => {
  const filters: Record<string, string> = {
    'entity_data->>billet': `eq.${workforceCategory}`,
    select: 'id',
  };
  if (section) {
    filters['entity_data->>unit'] = `eq.${section}`;
  }

  const persons = await entityService.listAll({ type: 'person', filters });
  return persons.map((person) => person.id);
};

/** person_sizing rows (catalog_id + parent_id) for the given person ids, batched to avoid URL length limits. */
const findPersonSizingRows = async (personIds: string[]): Promise<Entity[]> => {
  if (personIds.length === 0) return [];

  const batches = await Promise.all(
    chunk(personIds, 100).map((batch) =>
      entityService.listAll({
        type: 'person_sizing',
        filters: { 'entity_data->>person_id': buildIdFilter(batch), select: 'entity_data' },
      }),
    ),
  );

  return batches.flat();
};

/** Parent catalog entities (for NSN + display name), batched to avoid URL length limits. */
const findParentCatalogs = async (parentIds: string[]): Promise<Entity[]> => {
  if (parentIds.length === 0) return [];

  const batches = await Promise.all(
    chunk(parentIds, 75).map((batch) =>
      entityService.listAll({
        type: 'catalog',
        filters: { id: buildIdFilter(batch) },
      }),
    ),
  );

  return batches.flat();
};

export const runTotalClothingSizeByWorkforceCategoryReport = async (
  params: Record<string, unknown>,
): Promise<void> => {
  const workforceCategory = normalizeStringParam(params.workforceCategory);
  if (!workforceCategory) {
    throw new Error('Please select a Workforce Category.');
  }
  const section = normalizeStringParam(params.section);
  const parentNsnFilter = new Set(normalizeStringArrayParam(params.parentNsnIds));

  const personIds = await findApplicablePersonIds(workforceCategory, section);
  const sizingRows = await findPersonSizingRows(personIds);

  // Map catalog_id (sized variant) -> parent_id, restricted to the selected Parent NSNs (if any).
  const parentIdByCatalogId = new Map<string, string>();
  const catalogIds: string[] = [];
  const allParentIds = new Set<string>();

  for (const row of sizingRows) {
    const data = (row.entity_data ?? {}) as PersonSizingEntityData;
    const catalogId = typeof data.catalog_id === 'string' ? data.catalog_id : null;
    const parentId = typeof data.parent_id === 'string' ? data.parent_id : null;
    if (!catalogId || !parentId) continue;
    if (parentNsnFilter.size > 0 && !parentNsnFilter.has(parentId)) continue;

    parentIdByCatalogId.set(catalogId, parentId);
    catalogIds.push(catalogId);
    allParentIds.add(parentId);
  }

  const parentIds = Array.from(allParentIds);

  const [childCatalogOptions, parentCatalogs] = await Promise.all([
    iemService.getCatalogChildren(parentIds),
    findParentCatalogs(parentIds),
  ]);

  const sizeLabelByCatalogId = new Map<string, string>();
  for (const option of childCatalogOptions) {
    sizeLabelByCatalogId.set(option.id, option.sizeLabel ?? 'Unspecified');
  }

  const parentInfoById = new Map<string, { nsn: string | null; displayName: string }>();
  for (const parent of parentCatalogs) {
    const entityData = (parent.entity_data ?? {}) as Record<string, unknown>;
    const nsn =
      typeof entityData.nsn === 'string' && entityData.nsn.trim().length > 0
        ? entityData.nsn.trim()
        : null;
    parentInfoById.set(parent.id, {
      nsn,
      displayName: parent.display_name ?? 'Unknown Item',
    });
  }

  // Group: parentId -> size -> count of person_sizing rows with that size.
  const countsByParent = new Map<string, Map<string, number>>();
  for (const catalogId of catalogIds) {
    const parentId = parentIdByCatalogId.get(catalogId);
    if (!parentId) continue;
    const sizeLabel = sizeLabelByCatalogId.get(catalogId) ?? 'Unspecified';

    if (!countsByParent.has(parentId)) {
      countsByParent.set(parentId, new Map());
    }
    const sizeMap = countsByParent.get(parentId)!;
    sizeMap.set(sizeLabel, (sizeMap.get(sizeLabel) ?? 0) + 1);
  }

  const groups: ParentNsnSizeGroup[] = Array.from(countsByParent.entries())
    .map(([parentId, sizeMap]) => {
      const info = parentInfoById.get(parentId);
      return {
        parentCatalogId: parentId,
        nsn: info?.nsn ?? null,
        displayName: info?.displayName ?? 'Unknown Item',
        sizeCounts: Array.from(sizeMap.entries())
          .map(([size, count]) => ({ size, count }))
          .sort((a, b) => a.size.localeCompare(b.size)),
      };
    })
    .sort((a, b) => (a.nsn ?? a.displayName).localeCompare(b.nsn ?? b.displayName));

  const html = buildTotalClothingSizeByWorkforceCategoryHtml({
    generatedAt: new Date(),
    workforceCategory,
    section,
    groups,
  });

  outputReport(
    html,
    'total-clothing-size-by-workforce-category.html',
    getOutputFormat(params),
    getPreOpenedWindow(params),
  );
};
